import type { Permission } from '@/types';
import type { DocsLinkKey } from '@/features/help';

export const getVisibleSteps = (
  steps: ReadonlyArray<TourStep>,
  permissions: ReadonlyArray<Permission>,
): TourStep[] =>
  steps.filter(
    (s) => !s.requiresPermission || permissions.includes(s.requiresPermission),
  );

/**
 * Per-step layout discriminator. The TourCard component uses this to dispatch
 * to either `<TourCardCompact />` (default, ~400px anchored popover) or
 * `<TourCardSplit />` (~640px two-pane callout for content-rich steps). When
 * the viewport is too narrow for split, the dispatcher falls back to compact.
 */
export type TourCardLayout = 'compact' | 'split';

/**
 * Card placement around the spotlight target. Mirrors Floating UI's
 * `Placement` type — the four cardinal sides plus `-start` / `-end`
 * variants that pin one edge of the card to the corresponding edge of
 * the target instead of centering. Use `-end` to keep card bottoms
 * aligned across multi-frame steps with varying body lengths.
 */
export type TourPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

/**
 * Discriminated union describing what the tour card's media slot renders.
 * Supersedes the older `previewImage` string field. `previewImage` continues
 * to work for one release as a deprecated alias that maps to
 * `{ type: 'image', src: previewImage, alt: '' }`. Consumers that explicitly
 * want no media should use `{ type: 'none' }` (or `previewImage: null` on a
 * frame to clear the inherited step-level media).
 *
 * `aspect` defaults to `'16:9'` when consumed; videos require an aspect so
 * the slot reserves space and the card height does not jump on load.
 */
export type TourMedia =
  | {
      type: 'image';
      src: string;
      src2x?: string;
      alt: string;
      aspect?: '16:9' | '4:3' | '3:4' | '9:16' | 'auto';
    }
  | {
      type: 'video';
      sources: { src: string; mime: 'video/mp4' | 'video/webm' }[];
      poster: string;
      alt: string;
      aspect?: '16:9' | '4:3' | '3:4' | '9:16';
    }
  | { type: 'none' };

export type TourFrame = {
  /** Overrides the step's title for this frame. */
  title?: string;
  /** Overrides the step's body for this frame. Omit to inherit the step's
   * body verbatim — `resolveFrame` falls back via `frame?.body ?? step.body`,
   * which lets a multi-frame step avoid duplicating its first frame's copy. */
  body?: string;
  /** Overrides the step's `data-tour` target for this frame. */
  targetSelector?: string;
  /**
   * Additional `data-tour` value to include in the spotlight cutout. The
   * overlay rect becomes the bounding union of the primary target and this
   * one. Useful when a frame's interaction opens a portal-rendered popover
   * (e.g. calendar dropdown) that lives outside the primary target's DOM
   * subtree but should be highlighted alongside it.
   */
  secondarySelector?: string;
  /** Mutates global state before this frame becomes active. Overrides the
   * step's `onEnter` when set; `resolveFrame` falls back to `step.onEnter`
   * only when this is undefined, so the step-level hook never fires
   * alongside a frame-level one. */
  onEnter?: () => void | Promise<void>;
  /**
   * Per-frame teardown hook. Mirrors `TourStep.onLeave` but at frame
   * granularity — fires when the tour transitions away from this frame
   * (next, back, jump-via-goToFrame, or step-boundary). Errors are caught
   * by the provider so a buggy onLeave cannot stall the tour.
   */
  onLeave?: () => void | Promise<void>;
  /** A string overrides the step's preview; `null` explicitly clears it (e.g. for text-only frames). */
  previewImage?: string | null;
  /** Per-frame typed media slot. Overrides step-level media when set. */
  media?: TourMedia;
  placement?: TourPlacement;
  /** Optional per-frame docs link, surfaced as a "Learn more" affordance
   * in TourCard. Either a known key from DOCS_LINKS or a raw URL. */
  docsLink?: DocsLinkKey | string;
  /**
   * Skip the apply effect's `scrollIntoView({block: 'center'})` call for the
   * resolved target. Frames that scroll their container manually in
   * `onEnter` (e.g. the trade-form sections, which scroll the modal-internal
   * form to put a section at a specific ratio) need this — otherwise the
   * automatic re-center fights the manual scroll, yielding a card that
   * overlaps its spotlit area.
   */
  disableScrollIntoView?: boolean;
  /**
   * Override the compact card's default 400px width. Used by frames whose
   * `media` is a wide screenshot (e.g. trade-form section captures —
   * 2200px native, ~5:1 aspect) so the image is rendered close to the
   * underlying form's width and its labels stay legible. Should be a
   * Tailwind width class such as `'w-[min(1100px,calc(100vw-32px))]'`.
   */
  cardWidthClass?: string;
};

export type TourStep = {
  id: string;
  /** TanStack Router target. Tour navigates here before showing the step. */
  route?: string;
  /** Value of the `data-tour` attribute on the target element. Empty → centered card. */
  targetSelector?: string;
  title: string;
  body: string;
  previewImage?: string;
  /** Step-level typed media slot. Frame-level `media` overrides this. */
  media?: TourMedia;
  /**
   * Layout discriminator. Defaults to `'compact'` at the consumer when
   * absent. Promote to `'split'` for content-rich, multi-frame steps that
   * benefit from a two-pane callout.
   */
  layout?: TourCardLayout;
  placement?: TourPlacement;
  /** Mutates global state (e.g. open a modal, switch a tab) before the step shows. */
  onEnter?: () => void | Promise<void>;
  /**
   * Cleanup hook fired when the tour transitions *away* from this step
   * (next, back, skip, complete). Co-locates teardown with setup so
   * inserting a new step between two existing ones can't accidentally
   * leak side effects (e.g. a modal opened on enter that the next step
   * forgot to close). Errors are caught and logged so a buggy onLeave
   * cannot stall the tour.
   */
  onLeave?: () => void | Promise<void>;
  /** Defaults to true when targetSelector is set. */
  waitForSelector?: boolean;
  /** Step is silently skipped if user.permissions doesn't include this. */
  requiresPermission?: Permission;
  /** When set, the step has multiple frames navigated by Next/Back inside the same step. */
  frames?: TourFrame[];
  /** Optional docs link for this step, surfaced as a "Learn more"
   * affordance in TourCard. Frame-level docsLink overrides this. */
  docsLink?: DocsLinkKey | string;
  /**
   * Tour version this step shipped in. Required so future "What's new" flows
   * can filter to steps where `addedInVersion > user.onboardingSeenVersion`
   * without having to bump every existing step. All initial-release steps
   * declare `addedInVersion: 1`.
   */
  addedInVersion: number;
};

export type ResolvedFrame = {
  title: string;
  body: string;
  targetSelector?: string;
  secondarySelector?: string;
  onEnter?: () => void | Promise<void>;
  previewImage?: string;
  placement?: TourPlacement;
  docsLink?: DocsLinkKey | string;
  disableScrollIntoView?: boolean;
  cardWidthClass?: string;
};

export const getFrameCount = (step: TourStep): number =>
  step.frames && step.frames.length > 0 ? step.frames.length : 1;

/**
 * Single console.warn per app session for the deprecated `previewImage`
 * alias. Module-scoped so multiple TourCard renders during a tour fire
 * exactly one console message in DevTools.
 */
let previewImageDeprecationWarned = false;
const warnDeprecatedPreviewImage = (): void => {
  if (previewImageDeprecationWarned) return;
  previewImageDeprecationWarned = true;
   
  console.warn(
    '[onboarding] `previewImage` is deprecated. Migrate tour steps/frames to ' +
      '`media: { type: "image", src, alt }` (or "video" / "none"). The legacy ' +
      'field will be removed in a future release.',
  );
};

/** Test-only escape hatch: reset the once-per-session warn flag so a test can
 * re-trigger the deprecation message in a fresh assertion. NOT exported from
 * the package's public surface. */
export const __resetPreviewImageWarnForTests = (): void => {
  previewImageDeprecationWarned = false;
};

/**
 * Resolve which TourMedia a given (step, frame) pair should render.
 * Priority (highest first):
 *   1. frame.media (typed)
 *   2. step.media (typed)
 *   3. frame.previewImage (deprecated alias) — `null` explicitly clears
 *   4. step.previewImage (deprecated alias)
 *   5. { type: 'none' }
 *
 * The deprecated alias path emits a single console.warn per app session.
 */
export const resolveMedia = (
  step: TourStep,
  frame: TourFrame | null,
): TourMedia => {
  if (frame?.media) return frame.media;
  if (step.media) return step.media;
  // Deprecated previewImage handling. `null` on a frame is an explicit clear
  // (e.g. text-only frames) and short-circuits before the step alias.
  const frameLegacy = frame?.previewImage;
  if (frameLegacy === null) return { type: 'none' };
  if (typeof frameLegacy === 'string') {
    warnDeprecatedPreviewImage();
    return { type: 'image', src: frameLegacy, alt: '' };
  }
  if (typeof step.previewImage === 'string') {
    warnDeprecatedPreviewImage();
    return { type: 'image', src: step.previewImage, alt: '' };
  }
  return { type: 'none' };
};

export const resolveFrame = (step: TourStep, frameIndex: number): ResolvedFrame => {
  const frame = step.frames?.[frameIndex];
  // previewImage: a string in the frame overrides; `null` clears; `undefined` (or no frame) falls back to step.
  const framePreview = frame?.previewImage;
  const previewImage =
    framePreview === null
      ? undefined
      : framePreview ?? step.previewImage;
  return {
    title: frame?.title ?? step.title,
    body: frame?.body ?? step.body,
    targetSelector: frame?.targetSelector ?? step.targetSelector,
    secondarySelector: frame?.secondarySelector,
    onEnter: frame?.onEnter ?? step.onEnter,
    previewImage,
    placement: frame?.placement ?? step.placement,
    docsLink: frame?.docsLink ?? step.docsLink,
    disableScrollIntoView: frame?.disableScrollIntoView,
    cardWidthClass: frame?.cardWidthClass,
  };
};
